/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

/**
 * Props for the PasswordRequirement component
 */
export interface PasswordRequirementProps {
    /** The password string to validate against requirements */
    password: string;
    /** Optional CSS class name for custom styling */
    className?: string;
    /** Optional ID for the container, used for aria-describedby linking */
    id?: string;
    /**
     * Heading level for the "Password Requirements" title, so the checklist slots into the
     * surrounding page outline without skipping a level (WCAG 1.3.1). Defaults to 4; pass the
     * level that sits one below the section heading this component is nested under. Constrained
     * to the six valid ARIA heading levels so an out-of-range aria-level (0, 7+) can't be set.
     */
    headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
}

/**
 * Internal interface for defining password requirements
 */
interface Requirement {
    /** Unique identifier for the requirement */
    id: string;
    /** Translation key for the requirement text */
    textKey: string;
    /** Function that validates if the password meets this requirement */
    validator: (password: string) => boolean;
}

/**
 * PasswordRequirement component that displays real-time password validation requirements.
 *
 * This component shows a checklist of password requirements with visual indicators
 * (check marks for met requirements, X marks for unmet requirements) that update
 * in real-time as the user types their password.
 *
 * @param props - The component props
 * @param props.password - The password string to validate against requirements
 * @param props.className - Optional CSS class name for custom styling
 * @param props.id - Optional ID for the container, used for aria-describedby linking
 *
 * @returns JSX element containing the password requirements checklist
 *
 * @example
 * ```tsx
 * import { PasswordRequirement } from '@/components/password-requirements';
 * import { useWatch } from 'react-hook-form';
 *
 * function PasswordForm() {
 *   const password = useWatch({ control, name: 'password' });
 *
 *   return (
 *     <div>
 *       <input
 *         type="password"
 *         aria-describedby="password-requirements"
 *         {...register('password')}
 *       />
 *       <PasswordRequirement password={password} id="password-requirements" />
 *     </div>
 *   );
 * }
 * ```
 */
export function PasswordRequirement({ password, className, id, headingLevel = 4 }: PasswordRequirementProps) {
    const { t } = useTranslation('account');

    /**
     * Array of password requirements to validate against
     */
    const requirements: Requirement[] = [
        {
            id: 'length',
            textKey: 'password.requirements.minLength',
            validator: (pwd) => pwd.length >= 8,
        },
        {
            id: 'uppercase',
            textKey: 'password.requirements.hasUppercase',
            validator: (pwd) => /[A-Z]/.test(pwd),
        },
        {
            id: 'lowercase',
            textKey: 'password.requirements.hasLowercase',
            validator: (pwd) => /[a-z]/.test(pwd),
        },
        {
            id: 'number',
            textKey: 'password.requirements.hasNumber',
            validator: (pwd) => /\d/.test(pwd),
        },
        {
            id: 'special',
            textKey: 'password.requirements.hasSpecial',
            validator: (pwd) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd),
        },
    ];

    const metCount = requirements.filter((requirement) => requirement.validator(password)).length;

    return (
        <div id={id} className={cn('space-y-2', className)}>
            <p role="heading" aria-level={headingLevel} className="text-sm font-medium text-foreground">
                {t('password.requirements.title')}
            </p>
            {/*
             * Announce the met-requirement count as it changes so screen reader users get
             * confirmation of each keystroke's effect without moving focus (WCAG 4.1.3). The
             * per-item sr-only text below only helps once a user navigates into the list; this
             * live region is what makes the update proactive.
             */}
            <p role="status" aria-live="polite" className="sr-only">
                {t('password.requirements.summary', { met: metCount, total: requirements.length })}
            </p>
            <ul role="list" className="space-y-1.5">
                {requirements.map((requirement) => {
                    const isValid = requirement.validator(password);
                    return (
                        <li
                            key={requirement.id}
                            className={cn(
                                'flex items-center gap-2 text-sm transition-colors',
                                isValid ? 'text-primary' : 'text-muted-foreground'
                            )}>
                            {isValid ? (
                                <Check className="h-4 w-4 text-primary" data-testid="check-icon" />
                            ) : (
                                <X className="h-4 w-4 text-muted-foreground" data-testid="x-icon" />
                            )}
                            <span>{t(requirement.textKey as never)}</span>
                            {/*
                             * The check / cross icon is decorative (aria-hidden) and the met/unmet state
                             * is otherwise carried only by icon shape and text colour, so a screen reader
                             * hears the requirement with no pass/fail state. Announce the state as
                             * visually-hidden text so it is available without colour (WCAG 1.1.1 / 1.4.1).
                             */}
                            <span className="sr-only">
                                {isValid
                                    ? t('password.requirements.statusMet')
                                    : t('password.requirements.statusNotMet')}
                            </span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

export default PasswordRequirement;
